import './App.css';
import Modal from './modal';

/**
 * ContentBox component for rendering a content box with various elements.
 * 
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.textAlign] - The text alignment for the content.
 * @param {Array} [props.currentQuestion] - An array of content objects to be rendered.
 * @param {Array} [props.modal] - An array of modal content objects. 
 * @param {boolean} [props.isModalActive] - Indicates if the modal is active.
 * @param {function} [props.setModalIsActive] - Function to set the modal's active state.
 * @param {string} [props.differential] - The differential value for the modal.
 * @param {string} [props.stepCode] - The step code for the modal.
 * @param {string} [props.errorCheck] - The key to check for errors in stepStatus.
 * @param {Object} [props.stepStatus] - An object containing step statuses.
 * @param {string} [props.icon] - The icon to be displayed.
 * @param {string} [props.title] - The title of the content box.
 * @param {string} [props.subTitle] - The subtitle of the content box.
 * @returns {JSX.Element} A React component representing the content box.
 */
function ContentBox(props) {
    let listContent;
    let listModal = [];
    let matchingStep;
    let [key, value] = '';
    let errorText = 'Possible error';

    // Set text alignment
    const divStyle = {
        textAlign: props.textAlign,
    };

    // Render questions based on the current question
    const contents = props.currentQuestion;
    if(contents && Array.isArray(contents)){
        listContent = contents.map((content, index) =>
            <p key={`content-${index}`} className="content" style={divStyle} dangerouslySetInnerHTML={{__html: content.content}}></p>
        )
    }

    // Render Modal
    const modals = props.modal;
    if(modals && Array.isArray(modals)){
      listModal = modals.map((modalContent, index) =>
        <Modal key={`modal-${index}`} currentQuestion={modalContent.content} label={modalContent.label} title={modalContent.title} isActive={props.isModalActive} setIsActive={props.setModalIsActive} differential={props.differential} stepCode={props.stepCode} calculatedPayCode={props.calculatedPayCode} jobTitle={props.jobTitle} hoursPerWeek={props.hoursPerWeek}/>
      )
    }

    // Show Step status on final step
    if(props.errorCheck){
      matchingStep = Object.entries(props.stepStatus).find(([key, value]) => key === props.errorCheck);
      if (matchingStep) {
        [key, value] = matchingStep;
      } else {
        // Handle the case when no matching step is found
        console.log('No matching step found');
      }
    }

    // If no errors
    if(value == 'pass'){
      errorText = 'No errors<br/>found';
    }

    // Format degreeType for display
    let formattedDegreeType;
    if (props.degreeType === 'base-differential') {
      formattedDegreeType = 'None';
    } else {
      formattedDegreeType = props.degreeType
        ? props.degreeType
            .replace(/-/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : '';
    }

    let longevityText = '';
    if(props.longevity){
        const longevityYears = Math.floor(props.longevity);
        if (props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist') {
            if (longevityYears === 0) {
                longevityText = "None";
            } else if (longevityYears <= 1) {
                longevityText = "A/B (first year)";
            } else if (longevityYears < 5) {
                longevityText = "A/B";
            } else if (longevityYears < 7) {
                longevityText = "A/B + 5 but less than 7";
            } else if (longevityYears < 10) {
                longevityText = "A/B + 7 but less than 10";
            } else if (longevityYears < 15) {
                longevityText = "A/B + 10 but less than 15";
            } else if (longevityYears < 20) {
                longevityText = "A/B + 15 but less than 20";
            } else if (longevityYears < 22) {
                longevityText = "A/B + 20 but less than 22";
            } else { // 22 <= longevityYears
                longevityText = "A/B + 22 years or more";
            }
        } else {
            if (longevityYears < 5) {
                longevityText = "None";
            } else if (longevityYears < 7) {
                longevityText = "5 but less than 7";
            } else if (longevityYears < 10) {
                longevityText = "7 but less than 10";
            } else if (longevityYears < 15) {
                longevityText = "10 but less than 15";
            } else if (longevityYears < 20) {
                longevityText = "15 but less than 20";
            } else if (longevityYears < 22) {
                longevityText = "20 but less than 22";
            } else { // 22 <= longevityYears
                longevityText = "22 years or more";
            }
        }
    }

  return (
    <>
     <div className="content-box flex flex-row">
        {props.icon && <div className='icon' id={props.icon}><span className='sr-only'>{props.icon} icon</span></div>}
        <div>
            {props.title && <h2 className="title" style={divStyle}>{props.title}</h2>}
            {props.subTitle && <h3 className="sub-title" style={divStyle}>{props.subTitle}</h3>}
            {['staff-nurse', 'head-nurse', 'supervisor-nurse', 'occupational-therapist', 'physical-therapist', 'audiologist'].includes(props.jobTitle) && (
                <>
                    {props.errorCheck === 'step-2-status' && <p className='content' style={divStyle}>{String(props.singleYearDifferential).split('+')[0].trim() === '' || String(props.singleYearDifferential).split('+')[0].trim() === "0" ? 'None' : `Experience Differential ${String(props.singleYearDifferential).split('+')[0].trim()}`}</p>}
                    {props.errorCheck === 'step-1-status' && <p className='content' style={divStyle}>{formattedDegreeType}</p>}
                    {props.errorCheck === 'step-3-status' && <p className='content' style={divStyle}>{longevityText}</p>}
                </>
            )}
            {listContent}
        </div>
        {props.errorCheck && <div className='error-wrapper'><div className={`error-check ${value}`}></div><p className={`content ${value}`} dangerouslySetInnerHTML={{__html: errorText}}></p>{value == "fail" && listModal}</div>}
      </div>
    </>
  );
}

export default ContentBox;